import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button, Skeleton } from '@/components/ui'
import { openClosingItems, useClosingMonth } from '@/hooks/useClosingMonth'
import { useDashboard } from '@/hooks/useDashboard'
import { useDashboardPropertyId } from '@/hooks/useDashboardScope'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { formatMonthYear, localToday } from '@/lib/date'
import { formatBaht } from '@/lib/money'

/**
 * EP-155: the accountant's books under the to-do card. One line for the
 * month being closed and one line of the current period's money (decision
 * D1: billed, received, unpaid; the owner keeps the full figures).
 *
 * The month being closed is `useClosingMonth`'s (decision D6).
 */

export function AccountantBooks() {
  const { t, i18n } = useTranslation('common')
  const { timezone, calendar } = useLocalizationConfig()
  const isDesktop = useIsDesktop()
  const closing = useClosingMonth(true)
  const propertyId = useDashboardPropertyId()
  const { data: stats } = useDashboard(propertyId)

  const label = (month: string) => formatMonthYear(new Date(`${month}-01T00:00:00`), { locale: i18n.language, calendar })
  const m = closing.manifest
  const open = m ? openClosingItems(m) : 0
  const currentMonth = localToday(timezone).slice(0, 7)

  return (
    <section className="space-y-3" data-testid="accountant-books">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="accountant-closing">
        <span className="text-sm font-semibold">{t('dashboard.closing.title', { month: label(closing.month) })}</span>
        {closing.isLoading || !m ? (
          <Skeleton className="h-4 w-32" />
        ) : (
          <span className={`text-sm ${open > 0 ? 'text-warning' : 'text-base-content/60'}`} data-testid="accountant-closing-open">
            {open > 0 ? t('dashboard.closing.open', { n: open }) : `✓ ${t('dashboard.closing.clear')}`}
            {m.expenses_wht_count > 0 && <span className="text-base-content/60"> · {t('dashboard.closing.wht', { n: m.expenses_wht_count })}</span>}
          </span>
        )}
        <Button as={Link} to={`/closing-package?month=${closing.month}`} size="sm" variant="primary" outline
          className={isDesktop ? 'ml-auto' : 'w-full'} data-testid="accountant-closing-action">
          {t('dashboard.closing.action')}
        </Button>
      </div>

      {stats && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-dashed border-base-300 pt-3 text-sm" data-testid="accountant-money">
          <span className="font-semibold">{t('dashboard.money.period', { period: label(currentMonth) })}</span>
          <span>{t('dashboard.money.billed')} <b className="tabular-nums">{formatBaht(stats.revenue.billed_this_month, { decimals: 0 })}</b></span>
          <span className="text-base-content/40" aria-hidden>·</span>
          <span>{t('dashboard.money.received')} <b className="tabular-nums">{formatBaht(stats.revenue.revenue_this_month, { decimals: 0 })}</b></span>
          <span className="text-base-content/40" aria-hidden>·</span>
          <span>{t('dashboard.money.unpaid')} <b className="tabular-nums">{formatBaht(stats.revenue.unpaid_this_month, { decimals: 0 })}</b></span>
          <Link to="/reports" className="ml-auto text-xs text-primary hover:underline whitespace-nowrap">{t('dashboard.view_all_reports')} →</Link>
        </div>
      )}
    </section>
  )
}
